import { api } from "../config/api";
import { post, postAllowHttpError } from "../api";

function ensureNumeric(val: string | number | null | undefined): number | string {
  if (val === null || val === undefined) return val as number;
  if (typeof val === "number" && !Number.isNaN(val)) return val;
  const n = Number(val);
  return Number.isNaN(n) ? val : n;
}

export interface GetClientDetailsPayload {
  client_id: number | string;
  device_token: string | null;
  session_id: string | null;
  business_id: string | number | null;
  fetch_aadhar_number?: boolean;
}

export interface ClientDetailPhoneNumber {
  phone_id?: number;
  country_code?: string;
  phone?: string;
  phone_type?: string;
  is_active?: boolean;
  is_verified?: boolean;
}

export interface ClientDetailAddress {
  address_id?: number;
  address?: string;
  address_type?: string;
  is_active?: boolean;
  is_verified?: boolean;
}

export interface ClientDetailGroup {
  client?: string;
  username?: string | null;
  client_id?: number;
  is_active?: boolean;
  group_id?: number;
  group_name?: string;
  group_shortcode?: string;
  is_registered?: boolean;
}

export interface ClientDetailActivityContact {
  business_id?: number;
  client_info_id?: number;
  username?: string;
  message?: string;
  created?: string;
  delete_comments?: string | null;
  delete_comment?: string | null;
  comment?: string | null;
}

export interface ClientDetailIdProofFile {
  id?: number | null;
  document?: string;
  original_filename?: string;
  sort_order?: number;
}

export interface ClientDetailIdProof {
  id?: number;
  proof_type?: string;
  doc_name?: string;
  doc_type?: string;
  file_key?: string;
  idproof?: string;
  uploaded_by?: string;
  uploaded_by_user_id?: string;
  uploaded_on?: string;
  file_size?: string;
  is_active?: boolean;
  file_count?: number;
  files?: ClientDetailIdProofFile[];
}

export interface ClientDetailsData {
  client_id: number | string;
  client_state?: string;
  client_type?: "person" | "business";
  email?: string;
  name?: string;
  org_name?: string | null;
  first_name?: string;
  middle_name?: string;
  surname?: string;
  user_id?: string;
  client_pan?: string;
  contacts?: unknown[];
  contacts_info?: { contacts?: unknown[] };
  groups?: ClientDetailGroup[];
  recent_cases?: number | string | unknown[] | null;
  addresses?: {
    addresses?: ClientDetailAddress[];
    addresses_log?: unknown[];
  };
  idproofs?: { idproofs?: ClientDetailIdProof[] };
  phone_numbers?: {
    phonenumbers?: ClientDetailPhoneNumber[];
    phonenumbers_log?: unknown[];
  };
  client_name?: string;
  profile_pic_url?: string | null;
  is_logged?: boolean;
  client_activities?: {
    activity_info?: { contacts?: ClientDetailActivityContact[] };
  };
  date_of_birth?: string;
  date_of_incorporation?: string;
  created_by?: string;
  created_on?: string;
  aadhar_number?: string;
  phone?: string;
  country_code?: string;
  address?: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_pan?: string | null;
  contact_countrycode?: string | null;
  contact_phone?: string | null;
}

export interface GetClientDetailsResponse {
  success: boolean;
  data?: {
    client_details?: ClientDetailsData;
  };
  meta?: Record<string, unknown>;
  errors?: string | string[];
  error_code?: string | null;
}

export interface GetClientGroupsNewPayload {
  client_id: number | string;
  device_token: string | null;
  session_id: string | null;
  business_id: string | number | null;
}

export interface GetClientGroupsNewResponse {
  success: boolean;
  data?: {
    client_groups_info?: {
      client_groups?: ClientDetailGroup[];
    };
  };
  meta?: Record<string, unknown>;
  errors?: string | string[];
  error_code?: string | null;
}

export interface ClientApiResponse {
  client_id: number | string;
  client_type?: string;
  first_name?: string;
  middle_name?: string;
  surname?: string;
  org_name?: string;
  client_pan?: string;
  aadhar_number?: string;
  client_state?: string;
  client_name?: string;
  country_code?: string;
  phone?: string;
  email?: string;
  date_of_birth?: string;
  date_of_incorporation?: string;
  address?: string;
  contact_name?: string;
  contact_email?: string;
  contact_pan?: string;
  contact_countrycode?: string;
  contact_phone?: string;
  [key: string]: unknown;
}

interface GetClientsArgs {
  business_id: string | null;
  device_token: string | null;
  session_id: string | null;
}

export interface GetClientsListArgs extends GetClientsArgs {
  page_number?: number;
  page_size?: number;
  aadhar_number?: string | null;
  adhar_number?: string | null;
  fetch_aadhar_number?: boolean;
}

export interface SearchClientsArgs extends GetClientsArgs {
  search_string: string;
  fetch_aadhar_number?: boolean;
  client_type?: string;
}

interface GetClientsResponse {
  success: boolean;
  data?: {
    clients?: ClientApiResponse[];
    clients_assesse?: ClientApiResponse[];
    pagination?: {
      total_clients?: number;
      current_page?: number;
      total_pages?: number;
      page_size?: number;
      has_next?: boolean;
      has_previous?: boolean;
    };
  };
  errors?: string | string[];
  meta?: Record<string, unknown>;
  error_code?: string | null;
}

export function mergeSearchClientsResponseRows(
  data: { clients?: ClientApiResponse[]; clients_assesse?: ClientApiResponse[] } | undefined | null
): ClientApiResponse[] {
  if (!data) return [];
  const primary = Array.isArray(data.clients) ? data.clients : [];
  const secondary = Array.isArray(data.clients_assesse) ? data.clients_assesse : [];
  const seen = new Set<string>();
  const out: ClientApiResponse[] = [];
  for (const row of [...primary, ...secondary]) {
    if (!row || row.client_id === undefined || row.client_id === null) continue;
    const key = String(row.client_id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function normalizeClientApiResponse(client: ClientApiResponse): ClientApiResponse {
  const r = client as Record<string, unknown>;
  const raw = client.aadhar_number ?? r.aadhar ?? r.adhar_number ?? r.adhar ?? r.aadhaar_number;
  if (raw === undefined || raw === null) return client;
  const s = String(raw).trim();
  if (!s) return client;
  return { ...client, aadhar_number: s };
}

export interface InviteClientArgs extends GetClientsArgs {
  client_id: number | string;
}

interface InviteClientResponse {
  success: boolean;
  errors?: string;
}

export interface DeleteClientArgs extends GetClientsArgs {
  client_id: number | string;
}

interface DeleteClientResponse {
  success: boolean;
  errors?: string;
}

export interface CreateClientPayload extends GetClientsArgs {
  client_id?: number | string;
  client_type: string;
  first_name: string;
  middle_name?: string;
  surname?: string;
  org_name?: string | null;
  client_pan: string;
  email: string;
  username: string;
  phone: string;
  country_code: string;
  address?: string;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_pan?: string | null;
  contact_countrycode?: string | null;
  contact_phone?: string | null;
  self_assessment?: string;
  date_of_birth?: string;
  aadhar_number?: string | null;
}

export interface CreateClientResponse {
  success: boolean;
  errors?: string | string[];
}

function createClient(payload: CreateClientPayload): Promise<CreateClientResponse> {
  const { business_id, device_token, session_id } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const normalized = {
    ...payload,
    business_id: ensureNumeric(business_id) as number,
    self_assessment: payload.self_assessment === "true" ? 1 : 0,
  };
  return post<CreateClientResponse>(api.CREATE_CLIENT, normalized);
}

function deleteClient(args: DeleteClientArgs): Promise<DeleteClientResponse> {
  const { business_id, device_token, session_id, client_id } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined
  ) {
    return Promise.reject("business_id, device_token, session_id and client_id are required");
  }
  return post<DeleteClientResponse>(api.DELETE_CLIENT, {
    business_id,
    device_token,
    session_id,
    client_id,
  });
}

function inviteClient(args: InviteClientArgs): Promise<InviteClientResponse> {
  const { business_id, device_token, session_id, client_id } = args;
  if (!business_id || !device_token || !session_id || client_id === null) {
    return Promise.reject("business_id, device_token, session_id and client_id are required");
  }
  return post<InviteClientResponse>(api.INVITE_CLIENT, {
    business_id,
    device_token,
    session_id,
    client_id,
  });
}

export interface CreatePhoneNumberArgs extends GetClientsArgs {
  client_id: number | string;
  country_code: string;
  phone: string;
  phone_type: string;
  is_active: boolean;
  updated_by?: string;
}

export interface CreatePhoneNumberResponse {
  success: boolean;
  message?: string;
  errors?: string | string[] | Record<string, unknown>;
}

function createPhoneNumber(args: CreatePhoneNumberArgs): Promise<CreatePhoneNumberResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    country_code,
    phone,
    phone_type,
    is_active,
    updated_by,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    !phone?.trim() ||
    !phone_type
  ) {
    return Promise.reject(
      "Required fields: business_id, device_token, session_id, client_id, phone, phone_type"
    );
  }
  const normalizedCountryCode = country_code?.startsWith("+")
    ? country_code
    : `+${country_code || "91"}`;
  return post<CreatePhoneNumberResponse>(api.CREATE_PHONE_NUMBER, {
    business_id,
    device_token,
    session_id,
    client_id,
    country_code: normalizedCountryCode,
    phone: phone.trim(),
    phone_type,
    is_active: !!is_active,
    updated_by: updated_by ?? "business",
  });
}

export interface UpdatePhoneNumberArgs extends GetClientsArgs {
  client_id: number | string;
  phone_id: number | string;
  country_code: string;
  phone: string;
  phone_type: string;
  is_active: boolean;
  updated_by?: string;
}

function updatePhoneNumber(args: UpdatePhoneNumberArgs): Promise<CreatePhoneNumberResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    phone_id,
    country_code,
    phone,
    phone_type,
    is_active,
    updated_by,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    phone_id === null ||
    phone_id === undefined ||
    !phone?.trim() ||
    !phone_type
  ) {
    return Promise.reject(
      "Required fields: business_id, device_token, session_id, client_id, phone_id, phone, phone_type"
    );
  }
  const normalizedCountryCode = country_code?.startsWith("+")
    ? country_code
    : `+${country_code || "91"}`;
  return post<CreatePhoneNumberResponse>(api.CREATE_PHONE_NUMBER, {
    business_id,
    device_token,
    session_id,
    client_id,
    phone_id,
    country_code: normalizedCountryCode,
    phone: phone.trim(),
    phone_type,
    is_active: !!is_active,
    updated_by: updated_by ?? "business",
  });
}

export interface DeletePhoneNumberArgs extends GetClientsArgs {
  client_id: number | string;
  phone_id: number | string;
  phone?: string;
  country_code?: string;
  phone_type?: string;
  updated_by?: string;
}

function deletePhoneNumber(args: DeletePhoneNumberArgs): Promise<CreatePhoneNumberResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    phone_id,
    phone,
    country_code,
    phone_type,
    updated_by,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    phone_id === null ||
    phone_id === undefined
  ) {
    return Promise.reject(
      "Required fields: business_id, device_token, session_id, client_id, phone_id"
    );
  }
  const normalizedCountryCode = country_code?.startsWith("+")
    ? country_code
    : country_code
      ? `+${country_code}`
      : "+91";
  return post<CreatePhoneNumberResponse>(api.CREATE_PHONE_NUMBER, {
    business_id,
    device_token,
    session_id,
    client_id,
    phone_id,
    phone: phone ?? "",
    country_code: normalizedCountryCode,
    phone_type: phone_type ?? "other",
    is_active: false,
    updated_by: updated_by ?? "business",
  });
}

export interface CreateAddressArgs extends GetClientsArgs {
  client_id: number | string;
  address: string;
  address_type: string;
  updated_by?: string;
}

export interface UpdateAddressArgs extends GetClientsArgs {
  client_id: number | string;
  address_id: number | string;
  address: string;
  address_type: string;
  updated_by?: string;
}

export interface DeleteAddressArgs extends GetClientsArgs {
  client_id: number | string;
  address_id: number | string;
  address?: string;
  address_type?: string;
  updated_by?: string;
}

export interface AddressApiResponse {
  success: boolean;
  errors?: string | string[] | Record<string, unknown>;
}

function createAddress(args: CreateAddressArgs): Promise<AddressApiResponse> {
  const { business_id, device_token, session_id, client_id, address, address_type, updated_by } =
    args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    !address?.trim() ||
    !address_type
  ) {
    return Promise.reject(
      "Required fields: business_id, device_token, session_id, client_id, address, address_type"
    );
  }
  return post<AddressApiResponse>(api.CREATE_ADDRESS, {
    business_id: ensureNumeric(business_id),
    device_token,
    session_id,
    client_id: ensureNumeric(client_id),
    address_id: "",
    full_address: address.trim(),
    address_type: address_type.trim().toLowerCase(),
    is_active: true,
    client_user_id: "",
    updated_by: updated_by ?? "business",
  });
}

function updateAddress(args: UpdateAddressArgs): Promise<AddressApiResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    address_id,
    address,
    address_type,
    updated_by,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    address_id === null ||
    address_id === undefined ||
    !address?.trim() ||
    !address_type
  ) {
    return Promise.reject(
      "Required fields: business_id, device_token, session_id, client_id, address_id, address, address_type"
    );
  }
  return post<AddressApiResponse>(api.CREATE_ADDRESS, {
    business_id: ensureNumeric(business_id),
    device_token,
    session_id,
    client_id: ensureNumeric(client_id),
    address_id,
    full_address: address.trim(),
    address_type: address_type.trim().toLowerCase(),
    client_user_id: "",
    updated_by: updated_by ?? "business",
  });
}

function deleteAddress(args: DeleteAddressArgs): Promise<AddressApiResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    address_id,
    address,
    address_type,
    updated_by,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    address_id === null ||
    address_id === undefined
  ) {
    return Promise.reject(
      "Required fields: business_id, device_token, session_id, client_id, address_id"
    );
  }
  return post<AddressApiResponse>(api.CREATE_ADDRESS, {
    business_id,
    device_token,
    session_id,
    client_id,
    address_id,
    full_address: address ?? "",
    address_type: address_type ?? "other",
    is_active: false,
    client_user_id: "",
    updated_by: updated_by ?? "business",
  });
}

export interface CreateOrUpdateClientContactArgs extends GetClientsArgs {
  client_id: number | string;
  assesse_id?: string;
  name: string;
  email: string;
  pan: string;
  phone: string;
  country_code: string;
  is_active?: boolean;
}

export interface ClientContactApiResponse {
  success: boolean;
  errors?: string;
}

function createOrUpdateClientContact(
  args: CreateOrUpdateClientContactArgs
): Promise<ClientContactApiResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    assesse_id,
    name,
    email,
    pan,
    phone,
    country_code,
    is_active = true,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    !name?.trim() ||
    !email?.trim() ||
    !pan?.trim() ||
    !phone?.trim()
  ) {
    return Promise.reject(
      "Required: business_id, device_token, session_id, client_id, name, email, pan, phone"
    );
  }
  const phoneDigits = String(phone).replace(/\D/g, "");
  const payload: Record<string, unknown> = {
    business_id: ensureNumeric(business_id),
    device_token,
    session_id,
    client_id: ensureNumeric(client_id),
    name: name.trim(),
    email: email.trim().toLowerCase(),
    pan: pan.trim(),
    phone: phoneDigits,
    country_code: (country_code ?? "").replace(/^\+/, "").trim() || "91",
    is_active: !!is_active,
  };
  if (assesse_id !== null && assesse_id !== undefined && assesse_id !== "") {
    payload.assesse_id = assesse_id;
  }
  return post<ClientContactApiResponse>(api.UPDATE_CLIENT_CONTACT, payload);
}

export interface GetClientContactsArgs extends GetClientsArgs {
  client_id: number | string;
}

export interface ClientContactItem {
  assesse_id?: string;
  name?: string;
  email?: string;
  phone?: string;
  pan?: string;
  country_code?: string;
  is_active?: boolean;
}

export interface GetClientContactsResponse {
  success?: boolean;
  data?: {
    contacts?: ClientContactItem[];
    contacts_info?: {
      contacts?: ClientContactItem[];
    };
  };
  errors?: string;
}

function getClientContacts(args: GetClientContactsArgs): Promise<GetClientContactsResponse> {
  const { business_id, device_token, session_id, client_id } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === undefined ||
    client_id === null
  ) {
    return Promise.reject("business_id, device_token, session_id and client_id are required");
  }
  return post<GetClientContactsResponse>(api.GET_CLIENT_CONTACTS, {
    business_id,
    device_token,
    session_id,
    client_id,
  });
}

export interface DeleteClientContactArgs extends GetClientsArgs {
  client_id: number | string;
  assesse_id: string;
  name?: string;
  email?: string;
  pan?: string;
  phone?: string;
  country_code?: string;
}

function deleteClientContact(args: DeleteClientContactArgs): Promise<ClientContactApiResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    assesse_id,
    name = "",
    email = "",
    pan = "",
    phone = "",
    country_code = "91",
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    !assesse_id
  ) {
    return Promise.reject("Required: business_id, device_token, session_id, client_id, assesse_id");
  }
  return post<ClientContactApiResponse>(api.UPDATE_CLIENT_CONTACT, {
    business_id,
    device_token,
    session_id,
    client_id,
    assesse_id,
    name,
    email,
    pan,
    phone: String(phone).replace(/\D/g, ""),
    country_code: String(country_code).replace(/^\+/, "").trim() || "91",
    is_active: false,
  });
}

export interface UploadIdProofArgs extends GetClientsArgs {
  client_id: number | string;
  idproof: string;
  uuid: string;
  proof_type: string;
  doc_name: string;
  updated_by?: string;
}

export interface UploadIdProofResponse {
  success: boolean;
  errors?: string | string[];
  data?: {
    document_id?: number;
    doc_name?: string;
    file_count?: number;
    succeeded?: number;
    failed?: number;
  };
}

function uploadIdProof(args: UploadIdProofArgs): Promise<UploadIdProofResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    idproof,
    uuid,
    proof_type,
    doc_name,
    updated_by = "business",
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    !idproof ||
    !uuid ||
    !proof_type ||
    !doc_name
  ) {
    return Promise.reject(
      "Required: business_id, device_token, session_id, client_id, idproof, uuid, proof_type, doc_name"
    );
  }
  return post<UploadIdProofResponse>(api.UPLOAD_ID_PROOF, {
    business_id,
    device_token,
    session_id,
    client_id,
    idproof,
    uuid,
    proof_type,
    doc_name,
    updated_by,
  });
}

export interface UploadIdProofBundleArgs extends GetClientsArgs {
  client_id: number | string;
  proof_type: string;
  doc_name: string;
  documents: Array<{
    document: string;
    uuid: string;
    original_filename?: string;
    sort_order?: number;
  }>;
  updated_by?: string;
}

function uploadIdProofBundle(args: UploadIdProofBundleArgs): Promise<UploadIdProofResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    proof_type,
    doc_name,
    documents,
    updated_by = "business",
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    !proof_type ||
    !doc_name ||
    !Array.isArray(documents) ||
    documents.length === 0
  ) {
    return Promise.reject(
      "Required: business_id, device_token, session_id, client_id, proof_type, doc_name, documents"
    );
  }
  return post<UploadIdProofResponse>(api.UPLOAD_ID_PROOF, {
    business_id,
    device_token,
    session_id,
    client_id,
    proof_type,
    doc_name,
    documents,
    updated_by,
  });
}

export interface DownloadClientDocumentZipArgs extends GetClientsArgs {
  document_id: number | string;
}

export interface DownloadClientDocumentZipResponse {
  success?: boolean;
  data?: {
    download_url?: string;
    file_name?: string;
    file_count?: number;
    document_id?: number;
  };
  errors?: string | string[];
  error_code?: number;
}

function downloadClientDocumentZip(
  args: DownloadClientDocumentZipArgs
): Promise<DownloadClientDocumentZipResponse> {
  const { business_id, device_token, session_id, document_id } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    document_id === null ||
    document_id === undefined ||
    document_id === ""
  ) {
    return Promise.reject("Required: business_id, device_token, session_id, document_id");
  }
  return post<DownloadClientDocumentZipResponse>(api.DOWNLOAD_CLIENT_DOCUMENT_ZIP, {
    business_id,
    device_token,
    session_id,
    document_id,
  });
}

export interface DeleteProofDocumentArgs extends GetClientsArgs {
  document_id: number | string;
  delete_comments?: string;
  client_id?: number | string;
  user_id?: number | string;
}

export interface DeleteProofDocumentResponse {
  success: boolean;
  errors?: string | string[] | Record<string, unknown>;
  message?: string;
}

function deleteProofDocument(args: DeleteProofDocumentArgs): Promise<DeleteProofDocumentResponse> {
  const {
    business_id,
    device_token,
    session_id,
    document_id,
    delete_comments = "",
    client_id,
    user_id,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    document_id === null ||
    document_id === undefined
  ) {
    return Promise.reject("business_id, device_token, session_id and document_id are required");
  }
  const body: Record<string, unknown> = {
    business_id: ensureNumeric(business_id),
    device_token,
    session_id,
    document_id,
    delete_comments,
  };
  if (client_id !== undefined && client_id !== null && client_id !== "") {
    const cid = ensureNumeric(client_id);
    body.client_id = cid !== undefined && cid !== null ? cid : client_id;
  }
  const uid =
    user_id !== undefined && user_id !== null && user_id !== "" ? String(user_id).trim() : "";
  if (uid) {
    body.user_id = uid;
    body.updated_by = "business";
  } else if (client_id !== undefined && client_id !== null && String(client_id).trim() !== "") {
    body.updated_by = "client";
  }
  return post<DeleteProofDocumentResponse>(api.DELETE_PROOF_DOCUMENT, body);
}

export interface BusinessClientGroupItem {
  client_group_id?: number | string;
  name?: string;
  shortcode?: string;
  description?: string;
  is_active?: boolean;
  total_clients?: number;
}

export interface GetBusinessClientGroupsResponse {
  success: boolean;
  data?: { client_groups?: BusinessClientGroupItem[] };
  errors?: string;
}

export interface UpdateClientGroupsNewArgs extends GetClientsArgs {
  client_id: number | string;
  client_groups: (number | string)[];
}

export interface UpdateClientGroupsNewResponse {
  success: boolean;
  errors?: string;
}

function getBusinessClientGroups(args: GetClientsArgs): Promise<GetBusinessClientGroupsResponse> {
  const { business_id, device_token, session_id } = args;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<GetBusinessClientGroupsResponse>(api.GET_BUSINESS_CLIENT_GROUPS, {
    business_id,
    device_token,
    session_id,
  });
}

function updateClientGroupsNew(
  args: UpdateClientGroupsNewArgs
): Promise<UpdateClientGroupsNewResponse> {
  const { business_id, device_token, session_id, client_id, client_groups } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined
  ) {
    return Promise.reject("business_id, device_token, session_id and client_id are required");
  }
  return post<UpdateClientGroupsNewResponse>(api.UPDATE_CLIENT_GROUPS_NEW, {
    business_id,
    device_token,
    session_id,
    client_id,
    client_groups: Array.from(client_groups),
  });
}

export interface BizNoteItem {
  notes_id?: number;
  business_id?: number;
  business_name?: string;
  created_by?: string;
  client_info?: string;
  notes?: string;
  created_at?: string;
}

export interface GetAllBizNotesArgs extends GetClientsArgs {
  client_id: number | string;
}

export interface GetAllBizNotesResponse {
  success?: boolean;
  data?: { biz_notes_info?: BizNoteItem[] };
  errors?: string | string[];
}

function getAllBizNotes(args: GetAllBizNotesArgs): Promise<GetAllBizNotesResponse> {
  const { business_id, device_token, session_id, client_id } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined
  ) {
    return Promise.reject("business_id, device_token, session_id and client_id are required");
  }
  return post<GetAllBizNotesResponse>(api.GET_ALL_BUSINESS_NOTES, {
    business_id,
    device_token,
    session_id,
    client_id,
  });
}

export interface CreateBizNotesArgs extends GetClientsArgs {
  client_id: number | string;
  notes: string;
}

export interface CreateBizNotesResponse {
  success?: boolean;
  data?: {
    biz_notes_info?: BizNoteItem;
    status?: { saved_status?: boolean };
  };
  errors?: string | string[];
}

function createBizNotes(args: CreateBizNotesArgs): Promise<CreateBizNotesResponse> {
  const { business_id, device_token, session_id, client_id, notes } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    notes === null
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, client_id and notes are required"
    );
  }
  return post<CreateBizNotesResponse>(api.CREATE_BUSINESS_NOTES, {
    business_id,
    device_token,
    session_id,
    client_id,
    notes: String(notes).trim() || "",
  });
}

export interface EditBizNotesArgs extends GetClientsArgs {
  client_id: number | string;
  client_notes_id: number | string;
  notes: string;
  edit_comments?: string;
}

export interface EditBizNotesResponse {
  success?: boolean;
  data?: {
    biz_notes_info?: BizNoteItem;
    status?: { saved_status?: boolean };
  };
  errors?: string | string[] | null;
}

function editBizNotes(args: EditBizNotesArgs): Promise<EditBizNotesResponse> {
  const {
    business_id,
    device_token,
    session_id,
    client_id,
    client_notes_id,
    notes,
    edit_comments,
  } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    client_notes_id === null ||
    client_notes_id === undefined
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, client_id and client_notes_id are required"
    );
  }
  return post<EditBizNotesResponse>(api.EDIT_BUSINESS_NOTES, {
    business_id,
    device_token,
    session_id,
    client_id,
    client_notes_id,
    notes: String(notes).trim() || "",
    edit_comments: edit_comments ?? "",
  });
}

export interface DeleteBizNotesArgs extends GetClientsArgs {
  client_id: number | string;
  client_notes_id: number | string;
  edit_comments?: string;
}

export interface DeleteBizNotesResponse {
  success?: boolean;
  data?: Record<string, unknown>;
  errors?: string | string[] | null;
}

function deleteBizNotes(args: DeleteBizNotesArgs): Promise<DeleteBizNotesResponse> {
  const { business_id, device_token, session_id, client_id, client_notes_id, edit_comments } = args;
  if (
    !business_id ||
    !device_token ||
    !session_id ||
    client_id === null ||
    client_id === undefined ||
    client_notes_id === null ||
    client_notes_id === undefined
  ) {
    return Promise.reject(
      "business_id, device_token, session_id, client_id and client_notes_id are required"
    );
  }
  return post<DeleteBizNotesResponse>(api.DELETE_BUSINESS_NOTES, {
    business_id,
    device_token,
    session_id,
    client_id,
    client_notes_id,
    edit_comments: edit_comments ?? "",
  });
}

export interface GetClientZipFilesArgs extends GetClientsArgs {
  file_state: string;
}

export interface GetClientZipFilesResponse {
  success?: boolean;
  data?: { clientzips?: unknown[] };
  errors?: string;
}

export interface SubmitClientZipFileArgs extends GetClientsArgs {
  zip_file: string;
  file_key: string;
  uuid: string;
}

export interface SubmitClientZipFileResponse {
  success?: boolean;
  errors?: string;
}

export interface BulkInviteClientExtractedArgs extends GetClientsArgs {
  business_clientzip_id: number;
}

export interface BulkInviteClientExtractedResponse {
  success?: boolean;
  errors?: string;
}

export interface InviteClientExtractedArgs {
  extracted_client_file_id?: number | string;
  extracted_excel_row_id?: number | string;
  business_clientzip_id?: number | string;
  device_token: string | null;
  session_id: string | null;
  business_id?: string | null;
}

export interface InviteClientExtractedResponse {
  success?: boolean;
  errors?: string;
}

function getClientZipFiles(args: GetClientZipFilesArgs): Promise<GetClientZipFilesResponse> {
  const { business_id, device_token, session_id, file_state } = args;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<GetClientZipFilesResponse>(api.GET_CLIENT_ZIP_FILES, {
    business_id,
    device_token,
    session_id,
    file_state,
  });
}

function submitClientZipFile(args: SubmitClientZipFileArgs): Promise<SubmitClientZipFileResponse> {
  const { business_id, device_token, session_id, zip_file, file_key, uuid } = args;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  return post<SubmitClientZipFileResponse>(api.CLIENT_ZIP_FILES, {
    business_id,
    device_token,
    session_id,
    zip_file,
    file_key,
    uuid,
  });
}

function bulkInviteClientExtracted(
  args: BulkInviteClientExtractedArgs
): Promise<BulkInviteClientExtractedResponse> {
  const { business_id, device_token, session_id, business_clientzip_id } = args;
  if (!device_token || !session_id) {
    return Promise.reject("device_token and session_id are required");
  }
  return post<BulkInviteClientExtractedResponse>(api.BULK_INVITE_CLIENT_EXTRACTED, {
    business_clientzip_id,
    device_token,
    session_id,
    ...(business_id !== null && business_id !== undefined && { business_id }),
  });
}

function inviteClientExtracted(
  args: InviteClientExtractedArgs
): Promise<InviteClientExtractedResponse> {
  const {
    extracted_client_file_id,
    extracted_excel_row_id,
    business_clientzip_id,
    business_id,
    device_token,
    session_id,
  } = args;
  if (!device_token || !session_id) {
    return Promise.reject("device_token and session_id are required");
  }
  return post<InviteClientExtractedResponse>(api.INVITE_CLIENT_EXTRACTED, {
    ...(extracted_client_file_id !== null &&
      extracted_client_file_id !== undefined && { extracted_client_file_id }),
    ...(extracted_excel_row_id !== null &&
      extracted_excel_row_id !== undefined && { extracted_excel_row_id }),
    ...(business_clientzip_id !== null &&
      business_clientzip_id !== undefined && { business_clientzip_id }),
    device_token,
    session_id,
    ...(business_id !== null && business_id !== undefined && { business_id }),
  });
}

function getClientDetails(payload: GetClientDetailsPayload): Promise<GetClientDetailsResponse> {
  const { client_id, device_token, session_id, business_id, fetch_aadhar_number } = payload;
  if (
    client_id === null ||
    client_id === undefined ||
    !device_token ||
    !session_id ||
    business_id === null ||
    business_id === undefined
  ) {
    return Promise.reject("client_id, device_token, session_id and business_id are required");
  }
  const body: Record<string, unknown> = {
    client_id,
    device_token,
    session_id,
    business_id,
  };
  if (fetch_aadhar_number === true) {
    body.fetch_aadhar_number = true;
  }
  return post<GetClientDetailsResponse>(api.GET_CLIENT_DETAILS, body);
}

function getClientGroupsNew(
  payload: GetClientGroupsNewPayload
): Promise<GetClientGroupsNewResponse> {
  const { client_id, device_token, session_id, business_id } = payload;
  if (
    client_id === null ||
    client_id === undefined ||
    !device_token ||
    !session_id ||
    business_id === null ||
    business_id === undefined
  ) {
    return Promise.reject("client_id, device_token, session_id and business_id are required");
  }
  return post<GetClientGroupsNewResponse>(api.GET_CLIENT_GROUPS_NEW, {
    client_id,
    device_token,
    session_id,
    business_id,
  });
}

export interface CheckClientGroupShortcodePayload {
  business_id: string | number | null;
  device_token: string | null;
  session_id: string | null;
  client_group_shortcode: string;
}

export interface CheckClientGroupShortcodeResponse {
  success?: boolean;
  message?: string;
  data?: {
    client_group_info?: {
      is_valid?: boolean;
    };
  };
  errors?: string | string[] | null;
}

function checkClientGroupShortcode(
  payload: CheckClientGroupShortcodePayload
): Promise<CheckClientGroupShortcodeResponse> {
  const { business_id, device_token, session_id, client_group_shortcode } = payload;
  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }
  const trimmed = client_group_shortcode.trim();
  if (!trimmed) {
    return Promise.reject("client_group_shortcode is required");
  }
  return postAllowHttpError<CheckClientGroupShortcodeResponse>(api.CHECK_CLIENT_GROUP_SHORTCODE, {
    business_id: ensureNumeric(business_id),
    client_group_shortcode: trimmed,
    device_token,
    session_id,
  });
}

export const clientsService = {
  getClients,
  getClientsList,
  normalizeClientApiResponse,
  mergeSearchClientsResponseRows,
  searchClients,
  getClientDetails,
  getClientGroupsNew,
  createClient,
  inviteClient,
  deleteClient,
  createPhoneNumber,
  updatePhoneNumber,
  deletePhoneNumber,
  createAddress,
  updateAddress,
  deleteAddress,
  getClientContacts,
  createOrUpdateClientContact,
  deleteClientContact,
  uploadIdProof,
  uploadIdProofBundle,
  downloadClientDocumentZip,
  deleteProofDocument,
  getAllBizNotes,
  createBizNotes,
  editBizNotes,
  deleteBizNotes,
  getBusinessClientGroups,
  updateClientGroupsNew,
  getClientZipFiles,
  submitClientZipFile,
  bulkInviteClientExtracted,
  inviteClientExtracted,
  checkClientGroupShortcode,
};

function getClients(args: GetClientsArgs): Promise<GetClientsResponse> {
  const { business_id, device_token, session_id } = args;

  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }

  return post<GetClientsResponse>(api.GET_CLIENTS, { business_id, device_token, session_id });
}

function searchClients(args: SearchClientsArgs): Promise<GetClientsResponse> {
  const { business_id, device_token, session_id, search_string, fetch_aadhar_number, client_type } =
    args;

  if (!business_id || !device_token || !session_id || !search_string?.trim()) {
    return Promise.reject("business_id, device_token, session_id and search_string are required");
  }

  const body: Record<string, unknown> = {
    business_id: ensureNumeric(business_id) as number | string,
    device_token,
    session_id,
    search_string: search_string.trim(),
    client_type: client_type?.trim() || "ALL",
  };
  if (fetch_aadhar_number === true) {
    body.fetch_aadhar_number = true;
  }

  return post<GetClientsResponse>(api.SEARCH_CLIENTS, body);
}

function getClientsList(args: GetClientsListArgs): Promise<GetClientsResponse> {
  const {
    business_id,
    device_token,
    session_id,
    page_number = 1,
    page_size = 100,
    aadhar_number,
    adhar_number,
    fetch_aadhar_number,
  } = args;

  if (!business_id || !device_token || !session_id) {
    return Promise.reject("business_id, device_token and session_id are required");
  }

  const body: Record<string, unknown> = {
    business_id,
    device_token,
    session_id,
    page_number,
    page_size,
  };

  if (fetch_aadhar_number === true) {
    body.fetch_aadhar_number = true;
  }
  if (
    aadhar_number !== undefined &&
    aadhar_number !== null &&
    String(aadhar_number).trim() !== ""
  ) {
    body.aadhar_number = String(aadhar_number).trim();
  }
  if (adhar_number !== undefined && adhar_number !== null && String(adhar_number).trim() !== "") {
    body.adhar_number = String(adhar_number).trim();
  }

  return post<GetClientsResponse>(api.GET_CLIENTS_LIST, body);
}
